import { useEffect, useState } from 'react';
import { FeatureCode } from '../../common/enums/common.enum';
import { getKindergartenFeatures, KindergartenFeatureDto } from '../../api/feature.api';

interface KindergartenFeatures {
  /** False until the answer is known, so nothing flashes on screen and then vanishes. */
  isLoading: boolean;
  isEnabled: (code: FeatureCode) => boolean;
}

/**
 * Which features a kindergarten has switched on.
 *
 * The admin panel used to offer every feature's screens to every kindergarten, and a
 * switched-off one announced itself only as a 403 after the admin had clicked into it.
 *
 * A failed request answers "off" rather than throwing: the worst case is a screen the
 * admin cannot reach being hidden, which is the same thing the backend would enforce
 * anyway. This decides what to offer, never what to permit - the guard does that.
 */
export const useKindergartenFeatures = (kindergartenId?: string): KindergartenFeatures => {
  const [features, setFeatures] = useState<KindergartenFeatureDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!kindergartenId) {
      setFeatures([]);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    getKindergartenFeatures(kindergartenId)
      .then(result => {
        if (!cancelled) setFeatures(result ?? []);
      })
      .catch(() => {
        if (!cancelled) setFeatures([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [kindergartenId]);

  return {
    isLoading,
    isEnabled: (code: FeatureCode) => features.some(feature => feature.code === code && feature.enabled)
  };
};
